'use client';

import { useTranslations } from 'next-intl';

import type { TipTapContent } from '@zealy/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '@zealy/design-system';
import { InfoLine, LightningFilled } from '@zealy/icons';

import { TipTapToContent } from '#components/Editor';

type SprintPreviewModalContentProps = {
  description?: string | null;
  rewards?: TipTapContent | null;
  usdcPool?: number;
  zapsPool?: number;
};

export const SprintPreviewModalContent = ({
  description,
  rewards,
  usdcPool = 0,
  zapsPool = 0,
}: SprintPreviewModalContentProps) => {
  const t = useTranslations('sprints');
  const hasZealyDistributedRewards = usdcPool > 0 || zapsPool > 0;
  const hasCustomRewards =
    rewards &&
    rewards.content?.some((item: any) =>
      item.content?.some((subItem: any) => subItem.text && subItem.text.length > 0),
    );

  return (
    <div className="flex flex-col relative -top-300 py-250 px-400 gap-300 bg-tertiary rounded-sm flex-grow">
      {description ? <p className="body-paragraph-lg text-secondary">{description}</p> : null}

      {(hasZealyDistributedRewards || hasCustomRewards) && (
        <div className="flex flex-col gap-200">
          <span className="body-paragraph-lg-bold text-primary">{t('rewards-header')}</span>

          {/* Zealy Distributed Rewards Section */}
          {hasZealyDistributedRewards && (
            <div>
              <div className="flex items-center gap-50 mb-150">
                <p className="body-component-sm-bold text-secondary uppercase tracking-wide">
                  Distributed by Zealy
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center -mt-[1px]">
                      <InfoLine
                        size={12}
                        className="text-tertiary hover:text-secondary cursor-help"
                      />
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent>
                        <p>These rewards are guaranteed and distributed by Zealy</p>
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="flex flex-row gap-100 max-w-[500px]">
                {usdcPool > 0 && (
                  <div className="flex-1 p-150 bg-component-tertiary rounded-component-md sm:max-w-[150px]">
                    <div className="flex items-center gap-100">
                      <img src="/nstatic/usdc-icon.png" alt="USDC" className="w-[20px] h-[20px]" />
                      <div className="flex flex-col">
                        <span className="body-component-sm text-tertiary">USDC Pool</span>
                        <span className="body-component-md-bold text-primary">
                          ${(usdcPool / 100).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {zapsPool > 0 && (
                  <div className="flex-1 p-150 bg-component-tertiary rounded-component-md sm:max-w-[150px]">
                    <div className="flex items-center gap-100">
                      <LightningFilled size={20} className="text-[#FFD12F]" />
                      <div className="flex flex-col">
                        <span className="body-component-sm text-tertiary">Zaps Pool</span>
                        <span className="body-component-md-bold text-primary">
                          {zapsPool.toLocaleString()} Zaps
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {hasZealyDistributedRewards && (
                <p className="text-tertiary body-component-sm mt-100">
                  The reward pool is distributed at the end of the sprint proportionally to the
                  amount of XP earned.
                </p>
              )}
            </div>
          )}

          {/* Community Distributed Rewards Section */}
          {hasCustomRewards && (
            <div>
              {hasZealyDistributedRewards && (
                <div className="flex items-center gap-50 mb-150 mt-200">
                  <p className="body-component-sm-bold text-secondary uppercase tracking-wide">
                    Distributed by Community
                  </p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="flex items-center -mt-[1px]">
                        <InfoLine
                          size={12}
                          className="text-tertiary hover:text-secondary cursor-help"
                        />
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent>
                          <p>These rewards are distributed by the community</p>
                        </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
              <div className="p-150 bg-component-tertiary rounded-component-md">
                <TipTapToContent content={rewards} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
